import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { storeExhibitionPath } from '@/shared/config';
import { formatCount, formatKrw } from '@/shared/lib';
import { cn } from '@/shared/ui';
import type { ExhibitionCardItem } from '../model/exhibition-view';

interface ExhibitionCardProps {
  readonly exhibition: ExhibitionCardItem;
  /**
   * 목록 맨 앞의 큰 카드 — 넓은 화면(lg)에서 사진 왼쪽 60%·글 오른쪽으로 눕힌다.
   * 좁은 화면에서는 일반 카드와 같은 세로 배치다.
   */
  readonly isFeatured?: boolean;
}

/**
 * 기획전 카드 — 각진 회색 사진 칸(4:3, 올리면 살짝 확대) 아래 납기 문구·굵은 이름,
 * 얇은 선 아래 라임 "N개부터" 표식·"N원부터" 시작 가격과 "기획전 보러 가기 →" 안내.
 * 카드 전체가 그 기획전만 걸러진 상품 목록으로 가는 단일 링크다.
 * 카드로 오는 기획전은 공개 상품이 1개 이상이라 이미지·최소 수량이 항상 있다(§5-4).
 */
export const ExhibitionCard = ({
  exhibition,
  isFeatured = false,
}: ExhibitionCardProps): ReactElement => (
  <Link
    to={storeExhibitionPath(exhibition.id)}
    className="group block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4"
  >
    <article
      className={cn(
        'flex h-full flex-col',
        isFeatured && 'lg:grid lg:grid-cols-[3fr_2fr] lg:items-end lg:gap-10',
      )}
    >
      {/* 큰 카드가 두 칸을 다 쓰는 sm~md 에서는 사진을 16:9 로 낮춰 화면을 덮지 않게 한다. */}
      <div
        className={cn(
          'aspect-[4/3] w-full overflow-hidden bg-muted/70',
          isFeatured && 'sm:aspect-[16/9] lg:aspect-[4/3]',
        )}
      >
        <img
          src={exhibition.imageUrl}
          // 카드 전체가 하나의 링크라 접근 가능한 이름에 이미 제목이 있다 — 이미지 alt 는 장식용으로 비운다.
          alt=""
          loading="lazy"
          className="size-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03] motion-reduce:transition-none"
        />
      </div>

      <div className={cn('flex flex-1 flex-col pt-5', isFeatured && 'lg:flex-none lg:pt-0')}>
        <p className="text-sm text-muted-foreground">{exhibition.leadTimeNote}</p>
        <h3
          className={cn(
            'mt-2 text-xl leading-snug font-bold tracking-[-0.02em] sm:text-2xl',
            isFeatured && 'lg:text-[clamp(1.75rem,2.6vw,2.5rem)] lg:leading-tight',
          )}
        >
          {exhibition.title}
        </h3>

        {/* 표식 줄은 카드 아래 끝에 붙인다 — 이름이 두 줄인 카드와 나란해도 같은 높이에 선다. */}
        <div className="mt-auto pt-5">
          <div className="flex items-center justify-between gap-3 border-t border-foreground/10 pt-4">
            <span className="flex flex-wrap items-center gap-2">
              <span className="bg-brand-lime px-1.5 py-0.5 text-xs font-semibold tabular-nums">
                {formatCount(exhibition.minOrderQty)}개부터
              </span>
              {/* 묶인 상품 중 가장 싼 시작 단가 — 기획전에서도 가격을 바로 보게 한다. */}
              <span className="text-sm font-semibold tabular-nums">
                {formatKrw(exhibition.baseUnitPrice)}부터
              </span>
            </span>
            <span className="text-sm font-semibold whitespace-nowrap">
              기획전 보러 가기{' '}
              <span
                aria-hidden
                className="inline-block transition-transform group-hover:translate-x-1 motion-reduce:transition-none"
              >
                →
              </span>
            </span>
          </div>
        </div>
      </div>
    </article>
  </Link>
);
